import React, { useEffect, useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import Message from "shared/Message";
import ButtonRounded from "shared/Button";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "contexts/auth";
import { environments } from "constants/environments";
import { IApiMetaData } from "interfaces/apiMetaData";
import { callApi } from "apiServices/callApi";
import { createCategorySchema, ICreateCategoryInputs } from "schema/category";
import { MultiSelect } from "primereact/multiselect";

interface DropdownItem {
  name: string;
  value: boolean | string;
}
interface IProps {
  onSave: () => void;
}
const AddModule = ({ onSave }: IProps) => {
  const [programs, setPrograms] = useState<DropdownItem[]>([]); // Use DropdownItem[] to ensure consistent typing
  const [isResponse, setIsResponse] = useState(false);
  const [result, setResult] = useState<any>({});
  const { sharedData } = useAuth();
  const categoryAPI =
    environments.domain + sharedData?.apisList?.WebSite?.Category?.General;
  const programsAPI =
    environments.domain + sharedData?.apisList?.WebSite?.Program?.General;

  // Fetch the available programs on component load
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response: IApiMetaData = await callApi(
          "GET",
          programsAPI,
          {},
          {},
          { select: "name" }
        );
        if (response?.Status === 200) {
          const programOptions = response?.Data?.Content?.map(
            (program: any) => ({
              name: program.name,
              value: program._id,
            })
          );
          setPrograms(programOptions);
        }
      } catch (error) {
        console.error("Error fetching programs:", error);
      }
    };
    fetchPrograms();
  }, [programsAPI]);

  // Form setup using react-hook-form
  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ICreateCategoryInputs>({
    resolver: zodResolver(createCategorySchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<ICreateCategoryInputs> = async (
    data: ICreateCategoryInputs
  ) => {
    setIsResponse(false);
    try {
      const response: IApiMetaData = await callApi(
        "POST",
        categoryAPI,
        data,
        {},
        {},
        false
      );
      setResult(response);
      if (response?.Status === 201) {
        onSave(); // Re-fetch the data in the parent component
      }
      setIsResponse(true);
    } catch (error) {
      console.error("Error saving category:", error);
      setResult({ Status: 500, Message: "An unexpected error occurred" });
      setIsResponse(true);
    }
  };

  const statusOptions: DropdownItem[] = useMemo(
    () => [
      { name: "Active", value: true },
      { name: "Not Active", value: false },
    ],
    []
  );

  return (
    <div className="grid justify-content-center">
      <div className="col-12">
        <div className="px-3">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-fluid formgrid p-4 grid  border-round-md">
              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="name">Name</label>
                <InputText
                  id="name"
                  type="text"
                  {...register("name", {
                    setValueAs: (v) => v?.trim(),
                  })}
                />
                {errors?.name && (
                  <small className="text-error text-red-600">
                    {errors.name.message}
                  </small>
                )}
              </div>

              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="status">Status</label>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Dropdown
                        id="status"
                        value={value}
                        options={statusOptions}
                        onChange={(e) => onChange(e.value)}
                        optionLabel="name"
                        optionValue="value"
                        placeholder="Select Status"
                      />
                      {errors?.isActive && (
                        <small className="text-error text-red-600">
                          {errors.isActive.message}
                        </small>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="field col-12">
                <label htmlFor="program">Programs</label>
                <Controller
                  name="program"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <MultiSelect
                        value={value || []} // Handle empty default value
                        options={programs}
                        onChange={(e) => onChange(e.value)}
                        optionLabel="name"
                        placeholder="Select Programs"
                        maxSelectedLabels={3}
                        className="w-full"
                      />
                      {errors?.program && (
                        <small className="text-error text-red-600">
                          {errors.program.message}
                        </small>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="text-right mt-4 ml-auto">
                <ButtonRounded
                  disabled={isSubmitting}
                  label="Save"
                  raised
                  severity="success"
                />
              </div>
            </div>
          </form>
        </div>
      </div>

      {isResponse && (
        <div className="col-8">
          <Message
            message={
              result?.Status === 201
                ? result?.Message
                : result?.response?.data?.Error
            }
            severity={result?.Status === 201 ? "success" : "error"}
          />
        </div>
      )}
    </div>
  );
};

export default AddModule;
